/**
 * Porta única do microfone pro ditado e pra gravação (spec 016).
 *
 * Desligado (padrão) ou com `mistura: false`: exatamente o
 * `getUserMedia({ audio })` de sempre, sem passo a mais. Ligado: abre os
 * microfones escolhidos em Configurações › Ditado por voz › Microfones e
 * devolve a mistura deles. Qualquer falha na mistura cai no microfone
 * padrão — o ditado nunca fica mudo por causa dela.
 *
 * Com a mistura, as restrições de quem pediu (eco, ruído) não chegam aos
 * aparelhos: cada aparelho abre do jeito que `mixer.ts` explica.
 *
 * Tudo que abre fica registrado em `ativos.ts` (o indicador da ficha).
 */

import { CHAVE_PADRAO, registrarCaptura, rotuloPadrao, type FonteRotulo, type UsoMic } from './ativos';
import { listMics } from './devices';
import { abrirMistura } from './mixer';
import { chaveMic, getMicsPrefs, resolveMics, type MicConfig } from './store';

export interface AbrirOpts {
  mistura?: boolean;
  /** Pra quê — aparece no indicador de microfones abertos. */
  uso?: UsoMic;
}

export function abrirMicrofone(audio: MediaTrackConstraints | true, opts: AbrirOpts = {}): Promise<MediaStream> {
  const uso = opts.uso ?? 'ditado';
  if (opts.mistura === false || !getMicsPrefs().ativo) return abrirPadrao(audio, uso);
  return abrirEscolhidos(audio, uso);
}

function abrirPadrao(audio: MediaTrackConstraints | true, uso: UsoMic, falhas: FonteRotulo[] = []): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({ audio }).then((stream) => {
    const label = stream.getAudioTracks()[0]?.label ?? '';
    registrarCaptura({ stream, uso, mistura: null, fontes: [{ chave: CHAVE_PADRAO, rotulo: rotuloPadrao(label), nome: label || 'Microfone padrão' }], falhas });
    return stream;
  });
}

const LADO = { L: 'esq.', R: 'dir.' } as const;

/** "Lapela", ou o nome do aparelho sem o "Microfone (…)" do Windows. */
export function rotuloDe(cfg: MicConfig): FonteRotulo {
  const base = cfg.onde || cfg.nome.replace(/^Microfone\s*\((.*)\)\s*$/i, '$1').trim() || cfg.nome;
  return { chave: chaveMic(cfg), rotulo: cfg.canal && !cfg.onde ? `${base} ${LADO[cfg.canal]}` : base, nome: cfg.nome };
}

async function abrirEscolhidos(audio: MediaTrackConstraints | true, uso: UsoMic): Promise<MediaStream> {
  const prefs = getMicsPrefs();
  const usados = resolveMics(prefs, await listMics()).filter((r) => r.cfg.usar);
  const rotulos = usados.map(({ cfg }) => rotuloDe(cfg));
  const mistura = usados.length
    ? await abrirMistura(
        usados.map(({ dev, cfg }) => ({
          chave: chaveMic(cfg),
          deviceId: dev.id,
          ...(cfg.canal ? { canal: cfg.canal } : {}),
          volumeDb: cfg.volumeDb,
          corte: cfg.corte,
          quem: cfg.quem,
        })),
        { limiarFalanteDb: prefs.limiarFalanteDb },
      )
    : null;
  if (mistura) {
    registrarCaptura({
      stream: mistura.stream,
      uso,
      mistura,
      fontes: rotulos.filter((r) => mistura.chaves.includes(r.chave)),
      falhas: rotulos.filter((r) => !mistura.chaves.includes(r.chave)),
    });
    return mistura.stream;
  }
  return abrirPadrao(audio, uso, rotulos);
}
